import { Stack, Typography } from "@mui/material";
import type { UseQueryResult } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { Card } from "~/components/Card";
import {
  DetailsLayout,
  HistoryCard,
  LockCard,
  ManageCard,
  RelatedResourceLink,
  ResourceFields,
} from "~/components/DetailsCards";
import {
  CheckboxField,
  Form,
  FormSkeleton,
  getChangedFields,
  ObjectField,
  TextField,
  useStudioForm,
} from "~/components/Form";
import { QueryRenderer } from "~/components/QueryRenderer";
import { useDeleteTopic, useTopic, useUpdateTopic } from "~/domain/datastores";
import { pick } from "~/lib/std";
import {
  makeEditTopicLocation,
  makeRecordsLocation,
  makeTopicsLocation,
  useTopicParams,
} from "~/paths";
import type { Topic } from "~/services/datastore";
import { selectData } from "~/utils";
import { editTopicSchema } from "../schemas";

export function TopicDetails() {
  const { topicId } = useTopicParams();

  const query = useTopic(topicId, { select: selectData });

  const generalSection = <GeneralSection query={query} />;
  const typeSection = <TypeSection query={query} />;
  const relatedResourcesSection = (
    <Stack spacing={2} useFlexGap>
      <Typography variant="h4" component="h2">
        Related Resources
      </Typography>
      <RelatedResourceLink
        text="Records"
        to={makeRecordsLocation({ topicId })}
      />
    </Stack>
  );
  const infoSection = <InfoSection query={query} />;
  const lockSection = (
    <LockCard
      resourceName="topic"
      query={query}
      updateMutation={useUpdateTopic(topicId)}
    />
  );
  const historySection = <HistoryCard query={query} />;
  const manageSection = (
    <ManageCard
      resourceName="topic"
      query={query}
      editLocation={makeEditTopicLocation({ topicId })}
      deleteMutation={useDeleteTopic(topicId)}
      getReadableName={(topic) => topic.name}
      listLocation={makeTopicsLocation()}
    />
  );

  return (
    <DetailsLayout
      primaryGridColumn={
        <>
          {generalSection}
          {typeSection}
          {relatedResourcesSection}
        </>
      }
      secondaryGridColumn={
        <>
          {infoSection}
          {lockSection}
          {historySection}
          {manageSection}
        </>
      }
      stack={
        <>
          {generalSection}
          {infoSection}
          {typeSection}
          {lockSection}
          {historySection}
          {manageSection}
          {relatedResourcesSection}
        </>
      }
    />
  );
}

function GeneralSection({ query }: { query: UseQueryResult<Topic> }) {
  return (
    <Card title="General">
      <ResourceFields
        query={query}
        fields={[
          { dataType: "id", accessor: "id" },
          { dataType: "text", accessor: "name" },
          { dataType: "log", accessor: "logId" },
          { dataType: "topic", accessor: "associatedTopicId" },
          { dataType: "timestamp", accessor: "startTime" },
          { dataType: "timestamp", accessor: "endTime" },
          { dataType: "bytes", accessor: "recordSize" },
          { dataType: "number", accessor: "recordCount" },
          { dataType: "bytes", accessor: "objectSize" },
          { dataType: "number", accessor: "objectCount" },
        ]}
      />
    </Card>
  );
}

function TypeSection({ query }: { query: UseQueryResult<Topic> }) {
  return (
    <Card title="Type">
      <ResourceFields
        query={query}
        fields={[
          { dataType: "text", accessor: "typeName" },
          { dataType: "text", accessor: "typeEncoding" },
          { dataType: "pre", accessor: "typeData" },
          { dataType: "text", accessor: "typeSchema" },
        ]}
      />
    </Card>
  );
}

function InfoSection({ query }: { query: UseQueryResult<Topic> }) {
  return (
    <Card>
      <QueryRenderer
        query={query}
        loading={<FormSkeleton shapes={["multiline", "multiline", "text"]} />}
        success={(topic) => <InfoSectionImpl topic={topic} />}
      />
    </Card>
  );
}

function InfoSectionImpl({ topic }: { topic: Topic }) {
  const updateTopic = useUpdateTopic(topic.id);

  const { enqueueSnackbar } = useSnackbar();

  const schema = editTopicSchema.pick({
    note: true,
    context: true,
    strict: true,
  });
  const FIELDS = schema.keyof().options;

  const {
    control,
    handleSubmit,
    formState: { dirtyFields },
    reset,
  } = useStudioForm({
    schema,
    defaultValues: pick(topic, FIELDS),
    onSubmit(values) {
      const changedFields = getChangedFields(values, dirtyFields);

      updateTopic.mutate(changedFields, {
        onSuccess(response) {
          enqueueSnackbar("Topic updated", { variant: "success" });

          reset(pick(response.data, FIELDS));
        },
        onError() {
          enqueueSnackbar("Unable to update topic", { variant: "error" });
        },
      });
    },
  });

  return (
    <>
      <Form
        onSubmit={handleSubmit}
        loading={updateTopic.isLoading}
        submitText="Save Changes"
        disabled={topic.locked}
      >
        <TextField control={control} name="note" multiline />
        <ObjectField control={control} name="context" />
        <CheckboxField control={control} name="strict" />
      </Form>
      {topic.locked && (
        <Typography variant="body2">
          Unlock this topic to make updates.
        </Typography>
      )}
    </>
  );
}
